import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BillingProjectionModal } from '../../components/BillingProjectionModal';
import { useModules } from '../../contexts/ModulesContext';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, CreditCard, MapPin, ExternalLink, Loader2, ArrowLeft, AlertCircle, Calendar, ChevronDown, ChevronUp, Calculator, Users, Package, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import usePublicPackages from '../../hooks/usePublicPackages';
import PackageSelectionCards from '../../components/PackageSelectionCards';
import PlanChangeModal from '../../components/PlanChangeModal';

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminSubscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // 🆕 BUG 2 FIX: Get ModulesContext for refreshing modules after payment
  const { refreshModules } = useModules();
  
  // Billing Catalog Hook – Preise aus MongoDB via API (Single Source of Truth)
  const { packages, getPrice, getGrossPrice, getEmployeesPerLocation, loading: pkgsLoading } = usePublicPackages();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [locationCount, setLocationCount] = useState(0);
  const [loadingLocations, setLoadingLocations] = useState(true);
  
  // 🆕 BUG 1 FIX: State for referral status
  const [isReferralUser, setIsReferralUser] = useState(false);
  const [loadingReferralStatus, setLoadingReferralStatus] = useState(true);
  
  // 🆕 Accordion state - Default COLLAPSED on mobile
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  
  // 🆕 Billing Projection Modal State (replaces old "Was-wäre-wenn" calculator)
  const [projectionModalOpen, setProjectionModalOpen] = useState(false);
  
  // Phase 2B.4: V2-Kunden Paketauswahl für Checkout
  const [v2SelectedPackage, setV2SelectedPackage] = useState('starter');
  const [v2BillingInterval, setV2BillingInterval] = useState('month');
  const [v2CheckoutLoading, setV2CheckoutLoading] = useState(false);

  // Phase 2B.7: Self-Service Plan-Wechsel (nur für aktive V2-Kunden)
  const [showPlanChange, setShowPlanChange] = useState(false);

  // ?onboarding=true – frisch registrierter Kunde kommt von Register.js
  const isOnboarding = searchParams.get('onboarding') === 'true';

  // 🆕 Employee Limits State
  const [employeeLimits, setEmployeeLimits] = useState({
    total_employees: 0,
    location_count: 0,
    free_limit: 0,
    extra_employees: 0,
    cost_per_month: 0,
    price_per_employee: 0.99
  });
  const [limitsLoading, setLimitsLoading] = useState(true);

  // 🔧 Calculate projected billing based on current configuration
  const calculateProjectedBilling = () => {
    // Guard: Ensure data is loaded
    if (!subscription || !employeeLimits) {
      return null;
    }

    try {
      const locationCount = subscription.current_location_count || subscription.location_count || 1;
      const totalEmployees = employeeLimits.total_employees || 0;
      
      // Location pricing: 19.99 first + 11.99 for each additional
      const locationCost = locationCount === 1 ? 19.99 : 19.99 + ((locationCount - 1) * 11.99);
      
      // Employee pricing: 10 free per location, then 0.99 per extra
      const freeEmployees = locationCount * 10;
      const extraEmployees = Math.max(0, totalEmployees - freeEmployees);
      const employeeCost = extraEmployees * 0.99;
      
      // Total before discount
      const subtotal = locationCost + employeeCost;
      
      // Stripe-Rabatt aus gecachten Daten anwenden (z.B. AFX100 = 100% off forever)
      let discount = 0;
      let discountPercent = 0;
      let discountLabel = '';
      const disc = subscription?.stripe_discount_info;
      if (disc) {
        if (disc.percent_off) {
          discountPercent = disc.percent_off;
          discount = subtotal * (disc.percent_off / 100);
          discountLabel = `${disc.name || 'Gutschein'} (${disc.percent_off}% Rabatt)`;
        } else if (disc.amount_off) {
          discount = Math.min(subtotal, disc.amount_off / 100);
          discountLabel = `${disc.name || 'Gutschein'} (${(disc.amount_off/100).toFixed(2)}€ Rabatt)`;
        }
      }
      
      // Final amount
      const total = Math.max(0, subtotal - discount);
      
      return {
        locationCount,
        totalEmployees,
        freeEmployees,
        locationCost,
        extraEmployees,
        employeeCost,
        subtotal,
        discount,
        discountPercent,
        discountLabel,
        total,
        isTrialing,
        currency: 'EUR'
      };
    } catch (error) {
      console.error('Error calculating projected billing:', error);
      return null;
    }
  };

  // Note: calculateSimulation function removed - now handled by BillingProjectionModal

  // 🆕 BUG 1 FIX: Check if user was referred by checking customer data
  const [isPreviouslyRegistered, setIsPreviouslyRegistered] = useState(false);
  const [isDealUser, setIsDealUser] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(true); // default true bis geprüft
  
  const checkReferralStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const user = response.data;
      if (user.customer_id) {
        const customerResponse = await axios.get(`${API}/api/admin/company`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const customer = customerResponse.data;
        const isReferred = Boolean(customer.referred_by);
        const isDeal = Boolean(customer.deal_coupon_eligible);
        setIsReferralUser(isReferred);
        setIsDealUser(isDeal);
        setIsPreviouslyRegistered(Boolean(customer.previously_registered));
        // Zahlungsmethode prüfen
        if (customer.has_stripe) {
          setHasPaymentMethod(Boolean(customer.has_payment_method));
        }
        // V2-Kunden: hat_payment_method aus Subscription (stripe_customer_id vorhanden = Karte hinterlegt)
        if (customer.billing_model === 'billing_v2') {
          // Wenn stripe_customer_id bekannt ist, wurde beim Checkout eine Karte hinterlegt
          const hasStripeCustomer = Boolean(customer.stripe_customer_id || customer.has_stripe);
          setHasPaymentMethod(hasStripeCustomer);
        }      }
    } catch (error) {
      console.error('Error checking referral status:', error);
      setIsReferralUser(false);
    } finally {
      setLoadingReferralStatus(false);
    }
  };

  useEffect(() => {
    // 🆕 Clear all previous toast notifications when entering subscription page
    // This prevents old error messages (e.g., from failed location creation) from appearing
    toast.dismiss();
    
    loadSubscription();
    loadUpcomingInvoice();
    loadLocations();
    loadEmployeeLimits();
    checkReferralStatus(); // 🆕 BUG 1 FIX: Check referral status
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  // 🆕 Welcome-back toast for previously registered users (show once per session)
  useEffect(() => {
    if (!loadingReferralStatus && isPreviouslyRegistered) {
      const key = 'welcomed_back_shown';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        toast.info(
          'Willkommen zurück! Schön, dass Sie wieder bei AllFlowX sind. Ihr Abonnement wird ohne erneute Testphase direkt aktiviert.',
          { duration: 7000, position: 'top-center' }
        );
      }
    }
  }, [loadingReferralStatus, isPreviouslyRegistered]);

  const loadUpcomingInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/payments/upcoming-invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('🔍 Upcoming Invoice Response:', response.data);
      if (response.data.has_upcoming) {
        setUpcomingInvoice(response.data.invoice);
        console.log('✅ Upcoming Invoice set:', response.data.invoice);
      } else {
        console.log('📭 No upcoming invoice found');
      }
    } catch (error) {
      console.error('❌ Error loading upcoming invoice:', error);
      // Don't show error toast - upcoming invoice is optional
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocationCount(response.data.length);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadEmployeeLimits = async () => {
    setLimitsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employee-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeLimits(response.data);
    } catch (error) {
      console.error('Error loading employee limits:', error);
    } finally {
      setLimitsLoading(false);
    }
  };

  // 🆕 BUG 3 FIX: Ensure token is valid before making API calls
  const ensureValidToken = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('❌ No token found - redirecting to login');
      toast.error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      navigate('/login', { state: { from: window.location.pathname + window.location.search } });
      return false;
    }
    
    try {
      // Validate token with /auth/me call
      await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ Token is valid');
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn('❌ Token expired or invalid - redirecting to login');
        toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.', { duration: 5000 });
        // Preserve the session_id in the URL for after login
        navigate('/login', { 
          state: { 
            from: window.location.pathname + window.location.search,
            message: 'Bitte melden Sie sich an, um Ihren Zahlungsstatus zu überprüfen.' 
          } 
        });
        return false;
      }
      // Other errors (network, etc.) - continue anyway
      console.warn('⚠️ Token validation failed (non-401):', error.message);
      return true;
    }
  };

  const loadSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/payments/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_subscription) {
        const sub = response.data.subscription;
        setSubscription(sub);
        // V2: stripe_customer_id vorhanden = Karte beim Checkout hinterlegt
        if (sub?.billing_model === 'billing_v2' && sub?.stripe_customer_id) {
          setHasPaymentMethod(true);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15; // 15 attempts * 2s = 30 seconds
    const pollInterval = 2000;

    // 🆕 BUG 3 FIX: Validate token before first attempt
    if (attempts === 0) {
      console.log('🔍 Validating token before payment polling...');
      const isTokenValid = await ensureValidToken();
      if (!isTokenValid) {
        console.warn('❌ Token validation failed - stopping payment polling');
        setCheckingPayment(false);
        return;
      }
      console.log('✅ Token valid - starting payment polling');
    }

    if (attempts >= maxAttempts) {
      console.warn('⏱️ Payment polling timed out after 30 seconds');
      setCheckingPayment(false);
      
      // Check subscription status one final time
      try {
        const token = localStorage.getItem('token');
        const subResponse = await axios.get(
          `${API}/api/payments/subscription-status`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        if (subResponse.data.has_active_subscription) {
          toast.success('Abonnement aktiviert! Weiterleitung zum Dashboard...');
          setTimeout(() => navigate('/admin/dashboard', { replace: true }), 1000);
          return;
        }
      } catch (err) {
        console.error('Failed to check final subscription status:', err);
      }
      
      // If still no subscription, show error with manual redirect option
      toast.error(
        'Zahlungsprüfung dauert länger als erwartet. Bitte aktualisieren Sie die Seite oder gehen Sie zum Dashboard.',
        { duration: 8000 }
      );
      return;
    }

    setCheckingPayment(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payments/checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data.payment_status === 'paid' || 
          response.data.payment_status === 'no_payment_required' ||
          response.data.payment_status === 'unpaid' ||
          response.data.status === 'complete') {
        toast.success('Abonnement erfolgreich aktiviert!');
        setCheckingPayment(false);
        loadSubscription();
        
        // 🆕 BUG 2 FIX: Refresh ModulesContext after successful payment
        if (refreshModules) {
          refreshModules();
        }
        
        // Erfolgsmeldung kurz zeigen, dann zum Dashboard
        toast.success('✅ Zahlung bestätigt! Ihr Abonnement ist jetzt aktiv.');
        setTimeout(() => navigate('/admin', { replace: true }), 3000);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      // 🆕 BUG 3 FIX: Better error handling for 401
      if (error.response?.status === 401) {
        console.warn('❌ 401 during polling - token may have expired');
        setCheckingPayment(false);
        toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
        navigate('/login', { state: { from: window.location.pathname + window.location.search } });
        return;
      }
      
      setCheckingPayment(false);
      toast.error('Fehler bei der Zahlungsprüfung. Bitte aktualisieren Sie die Seite.');
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const frontendUrl = window.location.origin;

      // Legacy V1 → alter Endpoint unverändert
      const response = await axios.post(
        `${API}/api/payments/create-subscription-checkout`,
        { frontend_url: frontendUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Checkout-Session');
      setLoading(false);
    }
  };

  const handleV2Subscribe = async () => {
    setV2CheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/billing-v2/checkout/session`,
        {
          package_slug: v2SelectedPackage,
          billing_interval: v2BillingInterval,
          requested_location_slots: Math.max(1, subscription?.current_location_count || locationCount || 1),
          employee_addons: [],
          frontend_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      const msg = error.response?.data?.detail?.message || error.response?.data?.detail || 'Fehler beim Erstellen der Checkout-Session';
      toast.error(msg);
      setV2CheckoutLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/payments/create-portal-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening portal:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Öffnen des Kundenportals');
      setPortalLoading(false);
    }
  };

  // Calculate correct price (NEUES 2-Tier Modell: 19.99€ + 11.99€ pro weiteren Standort)
  const calculatePrice = (locationCount) => {
    if (locationCount === 0) return '0.00';
    
    let price = 19.99; // 1. Standort
    
    if (locationCount >= 2) {
      price += (locationCount - 1) * 11.99; // Alle weiteren Standorte je +11.99€
    }
    
    return price.toFixed(2);
  };

  if (loading && !checkingPayment) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-gray-600 dark:text-gray-400">Zahlungsstatus wird überprüft...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No subscription or pending_payment (Onboarding)
  if (!subscription || subscription.status === 'pending_payment') {
    const isPendingPayment = subscription?.status === 'pending_payment';
    
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">
              {isOnboarding ? '🎉 Fast fertig! Wählen Sie Ihr Paket' : 'AllFlowX Abonnement wählen'}
            </CardTitle>
            {isOnboarding && (
              <p className="text-sm text-muted-foreground mt-1">
                Ihr Konto ist angelegt. Wählen Sie jetzt das passende Paket und starten Sie direkt durch.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">

            {/* PENDING PAYMENT INFO */}
            {isPendingPayment && !isOnboarding && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <p className="font-semibold">Abonnement noch nicht aktiviert</p>
                    <p className="text-sm mt-1">
                      Sie können AllFlowX bereits nutzen. Bitte schließen Sie den Checkout ab, um Ihr Abonnement zu aktivieren und Rechnungen einsehen zu können.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Current Status */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">Ihre Standorte:</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{locationCount}</span>
              </div>
              {/* Monatspreis nur für Legacy-Kunden anzeigen (V2 Kunden sehen Preise in der Paketauswahl) */}
              {locationCount > 0 && subscription?.billing_model === 'legacy_v1' && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Monatspreis: <span className="font-bold text-green-600">{calculatePrice(locationCount)}€</span>
                </div>
              )}
            </div>

            {locationCount === 0 ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                    ⚠️ Sie müssen mindestens einen Standort anlegen, um ein Abonnement abzuschließen.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/admin/locations')}
                  size="lg"
                  className="w-full"
                  variant="outline"
                >
                  Standort hinzufügen →
                </Button>
              </div>
            ) : (
              <>
                {/* Phase 2B.6: Shared PackageSelectionCards Komponente */}
                <PackageSelectionCards
                  selectedPackage={v2SelectedPackage}
                  onSelectPackage={setV2SelectedPackage}
                  billingInterval={v2BillingInterval}
                  onIntervalChange={setV2BillingInterval}
                  onSubscribe={handleV2Subscribe}
                  loading={v2CheckoutLoading}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Has subscription
  const isTrialing = subscription.is_trialing === true || subscription.status === 'trialing';
  const isActive = subscription.status === 'active';
  const isCanceled = subscription.cancel_at_period_end;
  const isFullyCanceled = subscription.status === 'canceled';  // sofort/manuell gekündigt
  const subscriptionLocationCount = subscription.current_location_count || subscription.location_count || 1;
  // Billing Model: Legacy-Kunden behalten alte Preise, neue V2-Kunden sehen neue Preise
  const isLegacy = subscription?.billing_model !== 'billing_v2';
  const monthlyPrice = calculatePrice(subscriptionLocationCount);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-6 overflow-x-hidden">
      <Button
        variant="ghost"
        onClick={() => navigate('/admin')}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Zurück zum Dashboard
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isTrialing && <Clock className="h-6 w-6 text-blue-600" />}
            {isActive && !isCanceled && <CheckCircle2 className="h-6 w-6 text-green-600" />}
            {isCanceled && <XCircle className="h-6 w-6 text-red-600" />}
            Abonnement-Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6">
          
          {/* ── Vollständig gekündigt: klarer Status statt Abrechnungsvorschau ── */}
      {isFullyCanceled && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-600 shrink-0" />
              <div>
                <p className="font-semibold text-red-900 dark:text-red-100">Abonnement gekündigt</p>
                <p className="text-sm text-red-700 dark:text-red-300">Es wird keine weitere Abbuchung erfolgen. Alle Ihre Daten sind sicher und können exportiert werden.</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/exports-overview')}>
              Daten exportieren
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Normale Abrechnungsvorschau (nur für aktive/trialing Kunden) ── */}
      {!isFullyCanceled && (() => {
            const projected = calculateProjectedBilling();
            if (!projected) return null;

            // V2-Kunden: calculateProjectedBilling() verwendet Legacy-Preise → nur bei echtem upcomingInvoice anzeigen
            // Ohne echte Stripe-Invoice zeigen wir für V2 stattdessen die V2-Preisinfo
            if (!isLegacy && !upcomingInvoice) {
              const v2Price = getPrice(subscription.billing_v2_package || 'starter');
              if (!v2Price) return null;

              // Stripe-Rabatt aus stripe_discount_info berücksichtigen (z.B. ALI100 = 100%)
              const disc = subscription?.stripe_discount_info;
              const discPct = (disc?.valid && disc?.percent_off) ? disc.percent_off : 0;
              const effectiveNet = discPct >= 100 ? 0 : v2Price * (1 - discPct / 100);
              const effectiveGross = parseFloat((effectiveNet * 1.19).toFixed(2));

              return (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-800/20 border-2 border-blue-300 dark:border-blue-700 rounded-2xl shadow-lg">
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 md:gap-3 mb-3">
                      <div className="p-1.5 md:p-2 bg-blue-500 rounded-lg">
                        <Calendar className="h-4 w-4 md:h-6 md:w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-base md:text-xl font-bold text-gray-900 dark:text-white">
                          Monatlicher Abrechnungsbetrag
                        </h3>
                        <p className="text-xs md:text-sm text-blue-700 dark:text-blue-300">
                          Billing V2 · {subscription.billing_v2_package === 'professional_ai' ? 'Professional AI' : subscription.billing_v2_package === 'business' ? 'Business' : 'Starter'}
                          {discPct > 0 && ` · ${discPct}% Rabatt aktiv`}
                        </p>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border-2 border-blue-300 dark:border-blue-700">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Gesamtbetrag</span>
                        <span className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400">
                          {effectiveGross.toFixed(2)} EUR
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {effectiveNet.toFixed(2)} € Netto + {(effectiveNet * 0.19).toFixed(2)} € MwSt.
                        {discPct >= 100 && ' · Gutschein: kostenlos'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-indigo-900/20 dark:to-purple-800/20 border-2 border-indigo-300 dark:border-indigo-700 rounded-2xl shadow-lg">
                {/* Header - Always visible */}
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 md:gap-3 mb-3">
                    <div className="p-1.5 md:p-2 bg-indigo-500 rounded-lg">
                      <Calendar className="h-4 w-4 md:h-6 md:w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base md:text-xl font-bold text-gray-900 dark:text-white leading-tight">
                        Nächster Abrechnungsbetrag
                      </h3>
                      <p className="text-xs md:text-sm text-indigo-700 dark:text-indigo-300 leading-tight">
                        {upcomingInvoice ? 'Bestätigt von Stripe – inkl. Gutscheine' : 'Basierend auf Ihrer aktuellen Vertragskonfiguration'}
                      </p>
                    </div>
                  </div>

                  {/* Total Amount - Always visible */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border-2 border-indigo-300 dark:border-indigo-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-medium">Gesamtbetrag</span>
                      <span className="text-2xl md:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                        {(upcomingInvoice ? upcomingInvoice.amount_due : projected.total).toFixed(2)} {upcomingInvoice ? upcomingInvoice.currency : projected.currency}
                      </span>
                    </div>
                  </div>

                  {/* Toggle Button */}
                  <button
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                    className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-indigo-700 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100 transition-colors"
                  >
                    {detailsExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        Details ausblenden
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Details anzeigen
                      </>
                    )}
                  </button>
                </div>

                {/* Details - Collapsible */}
                {detailsExpanded && (
                  <div className="px-4 pb-4 md:px-5 md:pb-5 space-y-2 md:space-y-3 border-t border-indigo-200 dark:border-indigo-700 pt-3">
                    {/* Breakdown */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border border-indigo-200 dark:border-indigo-700">
                      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">KOSTENAUFSTELLUNG</div>
                      <div className="space-y-1.5 md:space-y-2">
                        <div className="flex items-center justify-between text-xs md:text-sm">
                          <span className="text-gray-900 dark:text-white">
                            {projected.locationCount} {projected.locationCount === 1 ? 'Standort' : 'Standorte'}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {projected.locationCost.toFixed(2)} €
                          </span>
                        </div>
                        {projected.extraEmployees > 0 && (
                          <div className="flex items-center justify-between text-xs md:text-sm">
                            <span className="text-gray-900 dark:text-white">
                              {projected.extraEmployees} Extra-Mitarbeiter à 0,99€ (Bestandstarif)
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {projected.employeeCost.toFixed(2)} €
                            </span>
                          </div>
                        )}
                        <div className="pt-1.5 md:pt-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between text-xs md:text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Zwischensumme</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {projected.subtotal.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                        {projected.discount > 0 && !upcomingInvoice?.discount && (
                          <div className="flex items-center justify-between text-xs md:text-sm bg-green-50 dark:bg-green-900/20 p-2 rounded">
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              🎟️ {projected.discountLabel || `Rabatt (${projected.discountPercent}%)`}
                            </span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              -{projected.discount.toFixed(2)} €
                            </span>
                          </div>
                        )}
                        {upcomingInvoice?.discount && (
                          <div className="flex items-center justify-between text-xs md:text-sm bg-green-50 dark:bg-green-900/20 p-2 rounded">
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              🎟️ Gutschein {upcomingInvoice.discount.percent != null ? `(${upcomingInvoice.discount.percent}% Rabatt)` : ''}
                            </span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              -{upcomingInvoice.discount.amount.toFixed(2)} €
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Info Banner */}
                    <div className="bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-700 rounded-lg p-2 md:p-3">
                      <p className="text-xs text-indigo-800 dark:text-indigo-200 leading-snug">
                        {upcomingInvoice
                          ? <>✅ <strong>Echtdaten von Stripe</strong> – inkl. aller Gutscheine und Rabatte.</>
                          : <>ℹ️ <strong>Hinweis:</strong> Dies ist eine Vorschau basierend auf Ihrer aktuellen Konfiguration.<span className="hidden md:inline"> Die tatsächliche Rechnung kann bei Änderungen abweichen.</span></>
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {/* Status Badge + Details + Actions – nur für aktive/trialing Kunden */}
      {!isFullyCanceled && (
      <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <p className="font-semibold text-lg">
                {isLegacy
                  ? 'AllFlowX Multi-Standort'
                  : `AllFlowX ${subscription.billing_v2_package === 'professional_ai' ? 'Professional AI' : subscription.billing_v2_package === 'business' ? 'Business' : 'Starter'}`
                }
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isTrialing && `Testphase (noch ${subscription.trial_days_remaining || 0} Tage)`}
                {isActive && !isCanceled && 'Aktiv'}
                {isCanceled && 'Läuft aus'}
              </p>
            </div>
            {isTrialing && (
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 text-sm rounded-full">
                Testphase
              </span>
            )}
            {isActive && !isCanceled && (
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 text-sm rounded-full">
                Aktiv
              </span>
            )}
          </div>

          {/* Current Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Standorte</p>
              <p className="text-2xl font-bold">{subscriptionLocationCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Preis</p>
              <p className="text-2xl font-bold">
                {isTrialing
                  ? '0,00€'
                  : isLegacy
                    ? `${monthlyPrice}€`
                    : getPrice(subscription.billing_v2_package || 'starter')
                      ? `${getPrice(subscription.billing_v2_package || 'starter').toFixed(2)}€`
                      : `${monthlyPrice}€`
                }
              </p>
            </div>
          </div>

          {/* Price Breakdown – nur für Legacy-Kunden */}
          {!isTrialing && isLegacy && (
            <div className="space-y-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm font-semibold mb-2">Preisaufschlüsselung (Netto):</p>
              <div className="flex justify-between text-sm">
                <span>1. Standort</span>
                <span>19,99 € <span className="text-xs text-muted-foreground font-normal">(Bestandstarif)</span></span>
              </div>
              {subscriptionLocationCount >= 2 && (
                <div className="flex justify-between text-sm">
                  <span>{subscriptionLocationCount - 1}× Weitere Standorte (je 11,99 € Bestandstarif)</span>
                  <span>{((subscriptionLocationCount - 1) * 11.99).toFixed(2)} €</span>
                </div>
              )}
              {!limitsLoading && employeeLimits.extra_employees > 0 && (
                <div className="flex justify-between text-sm text-orange-600 dark:text-orange-400 font-semibold">
                  <span>{employeeLimits.extra_employees}× Extra-Mitarbeiter (je 0,99 € Bestandstarif)</span>
                  <span>{employeeLimits.cost_per_month.toFixed(2)} €</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2 space-y-1">
                <div className="flex justify-between font-bold text-base">
                  <span>Netto-Summe / Monat</span>
                  <div className="text-right">
                    <span className="text-blue-600">{(parseFloat(monthlyPrice) + (employeeLimits.cost_per_month || 0)).toFixed(2)} €</span>
                    <p className="text-xs font-normal text-muted-foreground">zzgl. 19% MwSt.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 🆕 EMPLOYEE PRICING INFO */}
          {!limitsLoading && employeeLimits.location_count > 0 && (
            <div className={`p-4 rounded-lg border-2 ${
              employeeLimits.extra_employees > 0
                ? 'bg-orange-50 border-orange-300 dark:bg-orange-950/20 dark:border-orange-800'
                : 'bg-blue-50 border-blue-300 dark:bg-blue-950/20 dark:border-blue-800'
            }`}>
              <div className="space-y-2">
                <p className={`font-semibold ${
                  employeeLimits.extra_employees > 0 
                    ? 'text-orange-900 dark:text-orange-100' 
                    : 'text-blue-900 dark:text-blue-100'
                }`}>
                  Mitarbeiter-Preise
                </p>
                <div className={`text-sm ${
                  employeeLimits.extra_employees > 0 
                    ? 'text-orange-700 dark:text-orange-300' 
                    : 'text-blue-700 dark:text-blue-300'
                }`}>
                  <p>
                    <strong>{employeeLimits.location_count} Standorte</strong> × {isLegacy ? '10' : '15'} Mitarbeiter = <strong>{isLegacy ? employeeLimits.free_limit : employeeLimits.location_count * 15} inklusive</strong>
                  </p>
                  <p className="mt-1">
                    Aktuell: <strong>{employeeLimits.total_employees} Mitarbeiter</strong>
                  </p>
                  {employeeLimits.extra_employees > 0 ? (
                    <p className="mt-2 font-semibold text-orange-800 dark:text-orange-200">
                      ⚠️ {employeeLimits.extra_employees} zusätzliche Mitarbeiter {isLegacy ? `(+${employeeLimits.cost_per_month.toFixed(2)}€/Monat Bestandstarif)` : '(Add-on erforderlich)'}
                    </p>
                  ) : (
                    <p className="mt-2 text-green-600 dark:text-green-400">
                      ✓ Alle Mitarbeiter im inklusiven Kontingent
                    </p>
                  )}
                  {isLegacy ? (
                    <p className="text-xs mt-2 opacity-75">
                      Bestandstarif: Zusätzliche Mitarbeiter 0,99 € Netto/Monat
                    </p>
                  ) : (
                    <p className="text-xs mt-2 opacity-75">
                      Billing V2: Zusatzmitarbeiter als Add-on (+5/+10/+20 MA)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Zahlungsmethode fehlt – Warnung für Testkunden */}
          {isTrialing && !hasPaymentMethod && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-amber-500 text-xl shrink-0">⚠️</div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-900 dark:text-amber-300 text-sm">
                    Keine Zahlungsmethode hinterlegt
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 leading-relaxed">
                    Damit Ihr Abonnement nach der Testphase nicht unterbrochen wird, 
                    hinterlegen Sie bitte jetzt Ihre Zahlungsmethode (SEPA oder Kreditkarte).
                  </p>
                  <button
                    onClick={handleOpenPortal}
                    disabled={portalLoading}
                    className="mt-3 w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors"
                    data-testid="add-payment-method-btn"
                  >
                    {portalLoading ? 'Wird geöffnet...' : '➕ Zahlungsmethode jetzt hinterlegen'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {!isCanceled && (
            <div className="flex flex-col gap-3">
              {/* Plan-Wechsel-Button: nur für aktive V2-Kunden (nicht Legacy, nicht Complimentary) */}
              {!isLegacy && !isFullyCanceled && (
                <Button
                  onClick={() => setShowPlanChange(true)}
                  variant="default"
                  data-testid="plan-change-btn"
                  className="w-full"
                >
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Paket oder Add-ons wechseln
                </Button>
              )}

              {/* Legacy-Kunden: Support-Block statt Plan-Wechsel */}
              {isLegacy && !isFullyCanceled && (
                <div className="p-4 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 space-y-2">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                    Paket-Wechsel für Bestandstarif-Kunden
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Ihr Konto verwendet das Bestandstarif-Modell. Für Änderungen an Ihrem Tarif wenden Sie sich bitte direkt an uns.
                  </p>
                  <a
                    href="mailto:support@allflowx.de?subject=Paket-Wechsel Anfrage"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-200 underline underline-offset-2"
                    data-testid="legacy-support-link"
                  >
                    <ExternalLink className="h-3 w-3" />
                    support@allflowx.de kontaktieren
                  </a>
                </div>
              )}

              <Button onClick={handleOpenPortal} disabled={portalLoading} variant={hasPaymentMethod ? 'outline' : 'default'}>
                {portalLoading ? 'Öffnet Portal...' : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {hasPaymentMethod ? 'Rechnungen & Zahlungsmethode verwalten' : 'Zahlungsmethode hinterlegen'}
                  </>
                )}
              </Button>
            </div>
          )}
      </div>
      )}  {/* Ende !isFullyCanceled */}

          {/* 🆕 PROFESSIONAL BILLING PROJECTION BUTTON - Moved to end per UX requirements */}
        </CardContent>
      </Card>

      {/* Phase 2B.7: Plan-Wechsel-Modal (nur für aktive V2-Kunden) */}
      {!isLegacy && subscription && (
        <PlanChangeModal
          isOpen={showPlanChange}
          onClose={() => setShowPlanChange(false)}
          subscription={subscription}
          onSuccess={() => {
            setShowPlanChange(false);
            loadSubscription();
            loadEmployeeLimits();
            if (refreshModules) refreshModules();
            toast.success('Paket erfolgreich gewechselt! Die Änderungen sind sofort aktiv.');
          }}
        />
      )}

    </div>
  );
};
